<script setup lang="ts">
import { required, email } from '@vuelidate/validators'
import { storeToRefs } from 'pinia'
import { useValidation } from 'vue-composable'
import { formatError } from '~/utils/formatError'
import { logError } from '~/utils/logger'
import { SEGMENT_EVENTS } from '~/constants/segmentAnalytics'
import { ISignInResponse } from '~/api/types'
import LogRocket from 'logrocket'
import { platforms } from '~/constants/platforms'
import { twoFactorAuthRequired } from '~/helpers/twoFactorAuth'
import { isLinkedAccount } from '~/helpers/linkedAccount'

const navStore = useNavStore()
const mq = useMediaQuery()
const router = useRouter()
const registerStore = useRegisterStore()
const envStore = useEnvStore(),
  analyticsStore = useAnalyticsStore()
const { t: $t } = useI18n()
const hcaptchaToken = ref('')
const captchaExecutor = ref()

const handleVerification = (htoken: string) => {
  hcaptchaToken.value = htoken
}

const { email: userEmail, password } = storeToRefs(registerStore)
const isLoading = ref(false)

const errors = reactive({
  notAuthorizedException: false,
  tooManyAttemptsException: false,
})

navStore.setActiveStepId(0)
navStore.setWithoutStepperPageTitle($t('signinPage.title'))

//MetaData for SEO
useMetaData('signinPage')

onMounted(() => {
  analyticsStore.identify({})
  analyticsStore.track({ eventName: SEGMENT_EVENTS.signIn.PAGE_LOAD })
})

const formFields = reactive({
  password: password.value ?? '',
  emailField: userEmail.value ?? '',
})

const continueButtonDisabled = computed(() => {
  const formValid = pwdValidation.$anyInvalid || emailValidation.$anyInvalid
  return formValid
})

const emailValidation = useValidation({
  $value: toRef(formFields, 'emailField'),
  required: {
    $validator: required.$validator,
    $message: $t('signupPage.email.validation.required'),
  },
  email: {
    $validator: email.$validator,
    $message: $t('signupPage.email.validation.emailTypeText'),
  },
})
const pwdValidation = useValidation({
  $value: toRef(formFields, 'password'),
  required: {
    $validator: required.$validator,
    $message: $t('signinPage.password.validation.required'),
  },
})

const redirectToSignup = () => {
  router.push('/signup')
}

const onInput = () => {
  errors.notAuthorizedException = errors.tooManyAttemptsException = false
}

const handleLoginErrors = (response: ISignInResponse) => {
  switch (response.authenticationResult.code) {
    case 'CognitoUserNotFoundException': // without PreventUserExistenceErrors
      errors.notAuthorizedException = true
      break

    // with PreventUserExistenceError
    case 'CognitoNotAuthorizedException': {
      const message = response.authenticationResult.message
      if (message === 'Password attempts exceeded') {
        router.push('/signin/error')
        break
      }
      errors.notAuthorizedException = true
      sendSegmentEventOnSigninFailure()
      break
    }
    case 'CognitoTooManyRequestsException':
      errors.tooManyAttemptsException = true
      break
    case 'CognitoUserNotConfirmedException':
      if (registerStore.platform === platforms.XEMT) {
        registerStore.setAccountType('XERA')
      }
      registerStore.resendConfirmationCodeEmail()
      router.push('/signup/email-verification')
      break
    case 'CognitoPasswordResetRequiredException':
      // verification email will have been sent by Cognito
      router.push('/forgot-password/email-verification')
      break
    case 'CognitoUnknownError':
    default: {
      const innerError = response.authenticationResult.cognitoError?.code
      if (innerError === 'AccountClosedException') {
        sendSegmentEventOnLoginErrors('closed')
        router.push('/signin/closed')
      } else if (innerError === 'AccountLockedException') {
        sendSegmentEventOnLoginErrors('locked')
        router.push('/signin/locked')
      } else if (innerError === 'EmailNotVerifiedException') {
        // This case is for when dfxtoolbox changed an email - the email
        // has been changed in cognito, login-api has sent a confirmation code
        // and added a 'email not verified' flag to the id_token. So we want
        // to redirect to email-verification without resending a code.
        router.push('/signin/email-verification')
      } else {
        logError('Failed to login ' + innerError, 'page/siginin.onSubmit')
        sendSegmentEventOnLoginErrors('unknown')
        router.push('/signin/error')
      }
    }
  }
}

const sendSegmentEventOnSubmit = async (eventName: 'loginStarted' | 'loginSuccessful') => {
  if (eventName === 'loginStarted') {
    analyticsStore.track({
      eventName: SEGMENT_EVENTS.signIn.LOGIN_STARTED,
      properties: {
        loginMethod: SEGMENT_EVENTS.signIn.LOGIN_METHOD,
      },
    })
  }
  if (eventName === 'loginSuccessful') {
    await Promise.allSettled([
      analyticsStore.identify({
        traits: {
          userId: registerStore.profileId,
        },
      }),
      analyticsStore.track({
        eventName: SEGMENT_EVENTS.signIn.LOGIN_SUCCESSFUL,
        properties: {
          loginMethod: SEGMENT_EVENTS.signIn.LOGIN_METHOD,
        },
      }),
    ])
  }
}
const sendSegmentEventOnLoginErrors = (error: 'closed' | 'locked' | 'unknown') => {
  let eventName = ''
  switch (error) {
    case 'closed':
      eventName = SEGMENT_EVENTS.signIn.LOGIN_ERROR_ACCOUNT_CLOSED
      break

    case 'locked':
      eventName = SEGMENT_EVENTS.signIn.LOGIN_ERROR_ACCOUNT_LOCKED
      break

    case 'unknown':
      eventName = SEGMENT_EVENTS.signIn.LOGIN_ERROR_UNKNOWN
      break
    default:
      break
  }
  analyticsStore.track({
    eventName,
    properties: {
      location: SEGMENT_EVENTS.signIn.LOGIN_LOCATION,
    },
  })
}
const sendSegmentEventOnSigninFailure = () => {
  analyticsStore.track({
    eventName: SEGMENT_EVENTS.signIn.LOGIN_FAILED,
    properties: {
      failReason: 'credentials',
      loginMethod: 'email',
    },
  })
}

const sendChangeNumberCode = async () => {
  const twoFactorAuthStore = useTwoFactorAuthStore()

  try {
    const res = await twoFactorAuthStore.send2FACode('email', true)
    if (res.authenticationResult.success) {
      router.push(await useTwoFAPageRedirect(res))
    } else {
      router.push('/signin/error')
    }
  } catch {
    router.push('/signin/error')
  }
}

const onSubmit = async () => {
  isLoading.value = true
  registerStore.setEmail(formFields.emailField)

  if (captchaExecutor) {
    const ok = await captchaExecutor.value.executeCaptcha()
    if (!ok) {
      isLoading.value = false
      return
    }
  }

  if (window.xeSession) {
    window.xeSession.consentManager?.updateConsentState({ compliance: true })
  }
  registerStore.setPassword(formFields.password)
  sendSegmentEventOnSubmit('loginStarted')
  try {
    if (
      registerStore.redirectUrl &&
      (registerStore.redirectUrl.indexOf('help') !== -1 ||
        registerStore.redirectUrl.indexOf('zendesk') !== -1)
    ) {
      registerStore.setHelpcentre(true)
    } else {
      registerStore.setHelpcentre(false)
    }
    const response = await registerStore.signinEmail(hcaptchaToken.value, false)

    if (response?.authenticationResult?.success) {
      registerStore.setProfileId(response?.customerProfile?.id)
      await sendSegmentEventOnSubmit('loginSuccessful')

      LogRocket.identify(`${response.customerProfile?.id}`, {
        email: registerStore.email,
      })
      const twoFactorAuth = await twoFactorAuthRequired(response)
      let redirectUrl
      if (
        response?.helpcentreToken &&
        registerStore.helpcentre &&
        useRegisterStore().helpcentre &&
        response?.customerProfile?.id !== '' &&
        response?.customerProfile?.accountType === 'XERA'
      ) {
        redirectUrl =
          useEnvStore().getEnvVariable('VUE_APP_HELPCENTRE_LOGIN') +
          `${response?.helpcentreToken}&return_to=${useRegisterStore().redirectUrl}`
        window.location.href = redirectUrl
        return true
      }

      if (response?.helpcentreToken && registerStore.helpcentre && !twoFactorAuth) {
        redirectUrl = envStore.getEnvVariable('VUE_APP_HELPCENTRE_URL')
        window.location.href = redirectUrl
        return true
      }
      if (SetRedirection(response)) {
        return
      }

      // 2FA required?
      if (twoFactorAuth) {
        const twoFAPath = await useTwoFAPageRedirect(response)

        if (['/2fa/add-number', '/2fa/review-number'].includes(twoFAPath)) {
          sendChangeNumberCode()
          return
        }

        router.push(twoFAPath)
        return
      }

      // Linked account?
      if (await isLinkedAccount(response)) {
        router.push('/signin/account-selection')
        return
      }

      // SignIn success and continue to 2FA
      if (response.sessionId) {
        if (registerStore.redirectUrl) {
          window.location.href = registerStore.redirectUrlWithParams
        }
      }
    } else {
      handleLoginErrors(response)
      isLoading.value = false
    }
  } catch (exception) {
    const error = formatError(exception)
    isLoading.value = false

    logError('Failed to login ' + error.message, 'page/siginin.onSubmit')
    router.push('/signin/error')
  }
}
const SetRedirection = (response: ISignInResponse) => {
  if (
    registerStore.platform === platforms.XEMT &&
    response.customerProfile?.accountType === 'XERA'
  ) {
    router.push('/account-type')
    return true
  } else if (
    registerStore.platform === platforms.BRITLINE &&
    response.customerProfile?.accountType === 'XERA'
  ) {
    registerStore.setRedirectUrl(envStore.getEnvVariable('VUE_APP_URL_PERSONAL_REGISTRATION'))
  } else if (
    registerStore.platform === platforms.JOHN_LEWIS &&
    response.customerProfile?.accountType === 'XERA'
  ) {
    registerStore.setRedirectUrl(envStore.getEnvVariable('VUE_APP_URL_PERSONAL_REGISTRATION'))
  } else if (
    registerStore.platform === platforms.DEVPORTAL &&
    response.customerProfile?.accountType === 'XERA'
  ) {
    registerStore.setRedirectUrl(envStore.getEnvVariable('VUE_APP_URL_DEVPORTAL_REGISTRATION'))
    registerStore.setRedirectQueryParams(envStore.getEnvVariable('VUE_APP_DEVPORTAL_DFXID'))
  }
}
</script>

<template>
  <OenUiCard centered :sheet="true">
    <template v-if="mq.current !== 'xs'" #header>
      <OenUiCardHeader>
        <h1 class="type-h2">{{ $t('signinPage.title') }}</h1>
      </OenUiCardHeader>
    </template>
    <OenUiAlert v-if="errors.notAuthorizedException" theme="red" class="mb-4">
      <p class="error-message">{{ $t('signinPage.Incorrect') }}</p>
    </OenUiAlert>
    <OenUiAlert v-else-if="errors.tooManyAttemptsException" theme="red" class="mb-4">
      <p class="error-message">{{ $t('signinPage.ExceededLimit') }}</p>
    </OenUiAlert>
    <form @submit.prevent>
      <OenUiInputText
        v-model.trim="formFields.emailField"
        :label="$t('signupPage.email.label')"
        data-testId="signin-email"
        :validation="emailValidation"
        :placeholder="$t('signinPage.email.placeholder')"
        type="email"
        @input="onInput"
      />
      <OenUiInputPassword
        v-model.trim="formFields.password"
        :label="$t('signupPage.password.label')"
        :validation="pwdValidation"
        data-testId="signin-password"
        :placeholder="$t('signinPage.password.placeholder')"
        aria-label="password"
        @input="onInput"
      />
      <p class="text-center text-sm font-small mb-6 medium forgotText">
        <OenUiLink theme="primary" class="forgotText" @click="router.push('/forgot-password')">
          {{ $t('signinPage.ForgotPassword') }}
        </OenUiLink>
      </p>
      <XeciTermsandConditions v-if="registerStore.platform === platforms.XECI" from="signin" />

      <XeHCaptcha ref="captchaExecutor" location="Sign in" @verified="handleVerification" />

      <div class="footer">
        <OenUiButton
          :loading="isLoading"
          :disabled="continueButtonDisabled || isLoading"
          type="submit"
          @click="onSubmit"
        >
          {{ $t('component.button.continueText') }}
        </OenUiButton>

        <OenUiButton
          v-if="mq.current === 'xs'"
          theme="secondary"
          class="w-full !text-primary-text"
          @click="redirectToSignup"
        >
          {{ $t('signinPage.NotRegistered') }}
        </OenUiButton>

        <div v-else class="login-section text-center">
          <p>
            {{ $t('signinPage.NoAccount') }}
            <OenUiLink theme="primary" target="xe-signup" @click="redirectToSignup">
              {{ $t('signinPage.NotRegistered') }}
            </OenUiLink>
          </p>
        </div>
      </div>
    </form>
  </OenUiCard>
</template>

<style scoped>
.forgotText {
  font-size: 0.9375rem;
  line-height: 1.5rem;
}
.error-message {
  @apply text-red-text;
}
.submit-button {
  @apply fixed bottom-0 left-0;

  @screen sm {
    @apply static;
  }
}

.footer {
  @apply fixed bottom-0 flex flex-row-reverse  justify-between w-full left-0 items-baseline mt-12;

  button {
    @apply shadow-ria-1;
  }

  .login-section {
    @apply mt-6;
    p,
    a {
      @apply leading-6;
      font-size: 0.9375rem;
    }
  }
  @screen sm {
    @apply static block;
    button {
      @apply shadow-none;
    }
  }
}
</style>

<route lang="yaml">
meta:
  layout: withoutStepper
  showLeaveRegistrationImg: false
</route>
